import {
  Link,
  useParams
} from "react-router-dom";
import React, {
  useEffect,
  useMemo,
  useState
} from "react";
import {useAtom} from "jotai";

import {
  ADMIN_BLOCKED_CHILD_NAMES,
  DEFAULT_REPORT_LINKS,
  menuList,
  NORMAL_CHILD_MENU_NAMES,
  NORMAL_MAIN_MENU_NAMES,
  ROLE,
  SUPER_ADMIN_BLOCKED_CHILD_NAMES
} from "./entity";
import {
  AsideContainer,
  AsideFooter,
  AsideHeader,
  AsideRoot,
  BtnNarrow,
  CollapseButton,
  CollapseText,
  CustomReportRow,
  DropIcon,
  Logo,
  LogoCompact,
  LogoDescription,
  LogoTextGroup,
  Menu,
  MenuIcon,
  MenuItem,
  MenuLabel,
  MenuLink,
  MenuSection,
  MenuSectionLabel,
  Narrow,
  ReportDivider,
  SubMenu,
  SubMenuItem,
  SubMenuLink
} from "./styles";
import {
  reportsInfoAtom,
  tokenResultAtom
} from "../../pages/login/entity/Common";
import {
  retrieveCustomReportsList
} from "../../services/reports/ReportsAxios";
import {
  retrieveCustomReportsAdminList
} from "../../services/reports/ReportsAdminAxios";
import {
  DeleteReport
} from "../common/DeleteReport";
import {
  getRoleFlags
} from "../../common/roleUtils";

function MenuGlyph({name}) {
  const commonProps = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true
  };

  switch (name) {
    case "dashboard":
      return <svg {...commonProps}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>;
    case "campaign":
      return <svg {...commonProps}><path d="M5 21V4"/><path d="M5 5h11l-2 4 2 4H5"/></svg>;
    case "reports":
      return <svg {...commonProps}><path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/></svg>;
    case "settings":
      return <svg {...commonProps}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21H9.6v-.1A1.7 1.7 0 0 0 8.5 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3V9.6h.1A1.7 1.7 0 0 0 4.6 8.5a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3h4v.1A1.7 1.7 0 0 0 15.5 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 9c.2.4.6.8 1 1 .3.2.7.3 1.1.3h.1v4h-.1c-.4 0-.8.1-1.1.3-.4.1-.8.5-1 .4Z"/></svg>;
    case "platform":
      return <svg {...commonProps}><path d="m12 3 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 16 9 5 9-5"/></svg>;
    case "notice":
      return <svg {...commonProps}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/><path d="M10 21h4"/></svg>;
    default:
      return null;
  }
}

function AsideList({
  id,
  collapsed,
  role
}) {
  const [tokenUserInfo] =
      useAtom(tokenResultAtom);

  const [
    reportsInfo,
    setReportsInfo
  ] = useAtom(reportsInfoAtom);

  const [
    reportLists,
    setReportLists
  ] = useState([]);

  const {
    isNormalUser
  } = getRoleFlags(
      tokenUserInfo.role
  );

  const currentId = id ?? "";

  useEffect(() => {
    if (!tokenUserInfo.role) {
      return;
    }

    const fetchReports =
        isNormalUser
            ? retrieveCustomReportsList
            : retrieveCustomReportsAdminList;

    fetchReports(tokenUserInfo.id)
    .then(response => {
      setReportLists(
          Array.isArray(response)
              ? response
              : []
      );
    })
    .catch(error => {
      console.error(
          "커스텀 보고서 목록 조회 실패:",
          error
      );

      setReportLists([]);
    });
  }, [
    tokenUserInfo.role,
    tokenUserInfo.id,
    reportsInfo.id,
    isNormalUser
  ]);

  const availableMenus =
      useMemo(
          () =>
              menuList.filter(menu =>
                  canAccessMainMenu(
                      menu,
                      tokenUserInfo.role
                  )
              ),
          [tokenUserInfo.role]
      );

  const handleChangeReportsInfo =
      (reportId, groupBy) => {
        setReportsInfo(prev => ({
          ...prev,
          id: reportId,
          groupBy
        }));
      };

  const isActiveMenu = menu =>
      menu.include.includes(
          currentId
      );

  const isSelectedSubMenu =
      child =>
          currentId === child.name ||
          currentId === child.detail ||
          currentId === child.detail2 ||
          child.details?.includes(currentId);

  const renderDefaultReportLinks =
      () =>
          DEFAULT_REPORT_LINKS.map(
              report => {
                const selected =
                    currentId === report.id &&
                    Number(
                        reportsInfo?.id ?? 0
                    ) === 0;

                return (
                    <SubMenuItem
                        key={report.id}
                        $selected={selected}
                    >
                      <SubMenuLink
                          to={report.path}
                          $selected={selected}
                          onClick={
                            report.groupBy
                                ? () =>
                                    handleChangeReportsInfo(
                                        0,
                                        report.groupBy
                                    )
                                : () =>
                                    handleChangeReportsInfo(
                                        0,
                                        null
                                    )
                          }
                      >
                        {report.label}
                      </SubMenuLink>
                    </SubMenuItem>
                );
              }
          );

  const renderCustomReportLinks =
      () => {
        if (!reportLists.length) {
          return null;
        }

        return reportLists.map(
            report => {
              const selected =
                  currentId ===
                  "customReports" &&
                  report.id ===
                  reportsInfo.id;

              return (
                  <CustomReportRow
                      key={report.id}
                      $selected={selected}
                  >
                    <SubMenuLink
                        to="/board/customReports"
                        $selected={selected}
                        title={report.reportName}
                        onClick={() =>
                            handleChangeReportsInfo(
                                report.id,
                                report.groupByPeriod
                            )
                        }
                    >
                      {report.reportName}
                    </SubMenuLink>

                    {selected && (
                        <DeleteReport
                            reportsInfoId={
                              report.id
                            }
                        />
                    )}
                  </CustomReportRow>
              );
            }
        );
      };

  const renderReportsSubMenu =
      item => (
          <SubMenu
              $active={
                isActiveMenu(item)
              }
              $collapsed={collapsed}
              $itemCount={
                  DEFAULT_REPORT_LINKS
                      .length +
                  reportLists.length
              }
              $maxVisible={7}
          >
            {renderDefaultReportLinks()}

            {reportLists.length >
                0 && (
                    <ReportDivider />
                )}

            {renderCustomReportLinks()}
          </SubMenu>
      );

  const renderDefaultSubMenu =
      item => {
        const visibleChildren =
            item.child.filter(child =>
                canAccessChildMenu(
                    child,
                    role
                )
            );

        return (
            <SubMenu
                $active={
                  isActiveMenu(item)
                }
                $collapsed={collapsed}
                $itemCount={
                  visibleChildren.length
                }
            >
              {visibleChildren.map(
                  child => {
                    const selected =
                        isSelectedSubMenu(
                            child
                        );

                    return (
                        <SubMenuItem
                            key={child.name}
                            $selected={selected}
                        >
                          <SubMenuLink
                              to={`/board/${child.name}`}
                              $selected={selected}
                          >
                            {child.header}
                          </SubMenuLink>
                        </SubMenuItem>
                    );
                  }
              )}
            </SubMenu>
        );
      };

  return (
      <>
        {availableMenus.map(
            (item, index) => {
              const active =
                  item.name === "inquiry"
                      ? false
                      : isActiveMenu(item);
              const previousGroup =
                  availableMenus[index - 1]?.group;
              const showGroupLabel =
                  !collapsed && item.group !== previousGroup;

              return (
                <React.Fragment key={item.name}>
                  {showGroupLabel && (
                      <MenuSectionLabel $group={item.group}>
                        {item.groupLabel ?? item.group}
                      </MenuSectionLabel>
                  )}
                  <MenuItem
                      $active={active}
                      $collapsed={collapsed}
                      $support={item.group === "support"}
                  >
                    <MenuLink
                        to={`/board/${item.name}`}
                        $active={active}
                        $collapsed={collapsed}
                        $support={item.group === "support"}
                        $disabled={item.name === "inquiry"}
                        aria-disabled={item.name === "inquiry"}
                        tabIndex={item.name === "inquiry" ? -1 : undefined}
                        onClick={item.name === "inquiry" ? event => event.preventDefault() : undefined}
                        title={
                          collapsed
                              ? item.header
                              : undefined
                        }
                    >
                      <MenuIcon
                          $active={active}
                          $help={item.name === "inquiry"}
                          $disabled={item.name === "inquiry"}
                      >
                        {item.name !== "inquiry" && <MenuGlyph name={item.name}/>}
                      </MenuIcon>

                      <MenuLabel
                          $collapsed={
                            collapsed
                          }
                          $support={item.group === "support"}
                      >
                        {item.header}
                      </MenuLabel>

                      {!collapsed &&
                          item.child.length >
                          0 && (
                              <DropIcon
                                  $open={active}
                                  aria-hidden="true"
                              />
                          )}
                    </MenuLink>

                    {item.child.length >
                        0 &&
                        (item.name ===
                        "reports"
                            ? renderReportsSubMenu(
                                item
                            )
                            : renderDefaultSubMenu(
                                item
                            ))}
                  </MenuItem>
                </React.Fragment>
              );
            }
        )}
      </>
  );
}

function canAccessMainMenu(
    item,
    userRole
) {
  if (
      [
        ROLE.ADMIN,
        ROLE.SUPER_ADMIN
      ].includes(userRole)
  ) {
    return true;
  }

  if (
      userRole === ROLE.NORMAL
  ) {
    return (
        NORMAL_MAIN_MENU_NAMES.includes(
            item.name
        )
    );
  }

  return false;
}

function canAccessChildMenu(
    child,
    role
) {
  if (
      role === ROLE.SUPER_ADMIN
  ) {
    return (
        !SUPER_ADMIN_BLOCKED_CHILD_NAMES.includes(
            child.name
        )
    );
  }

  if (role === ROLE.ADMIN) {
    return (
        !ADMIN_BLOCKED_CHILD_NAMES.includes(
            child.name
        )
    );
  }

  if (role === ROLE.NORMAL) {
    return (
        NORMAL_CHILD_MENU_NAMES.includes(
            child.name
        )
    );
  }

  return false;
}

export default function Aside({mobileOpen = false, onNavigate}) {
  const params = useParams();

  const [
    collapsed,
    setCollapsed
  ] = useState(false);

  const [tokenUserInfo] =
      useAtom(tokenResultAtom);

  useEffect(() => {
    onNavigate?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const handleChangeWidth = () => {
    setCollapsed(prev => !prev);
  };

  return (
      <AsideRoot
          $collapsed={collapsed}
          $mobileOpen={mobileOpen}
      >
        <AsideContainer
            $collapsed={collapsed}
            $mobileOpen={mobileOpen}
        >
          <AsideHeader
              $collapsed={collapsed}
          >
            <Link
                to="/board/dashboard"
                aria-label="대시보드로 이동"
            >
              {collapsed ? (
                  <LogoCompact>
                    AD
                  </LogoCompact>
              ) : (
                  <LogoTextGroup>
                    <Logo />
                    <LogoDescription>광고 관리 플랫폼(Oaple-client)</LogoDescription>
                  </LogoTextGroup>
              )}
            </Link>
          </AsideHeader>

          <MenuSection>
            <Menu>
              <AsideList
                  id={params.id}
                  collapsed={collapsed}
                  role={
                    tokenUserInfo.role
                  }
              />
            </Menu>
          </MenuSection>

          <AsideFooter>
            <Narrow>
              <CollapseButton
                  type="button"
                  $collapsed={collapsed}
                  aria-label={
                    collapsed
                        ? "사이드바 펼치기"
                        : "사이드바 접기"
                  }
                  onClick={
                    handleChangeWidth
                  }
              >
                <BtnNarrow
                    $collapsed={
                      collapsed
                    }
                    aria-hidden="true"
                />

                <CollapseText
                    $collapsed={
                      collapsed
                    }
                >
                  메뉴 접기
                </CollapseText>
              </CollapseButton>
            </Narrow>
          </AsideFooter>
        </AsideContainer>
      </AsideRoot>
  );
}
